import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '@/hooks/useChat';
import { useChatHistory } from '@/hooks/useChatHistory';
import { useOptionalVmClient } from '@/api/context';
import { clearLastChat } from '@/hooks/useViewPersistence';
import { useChatInputContext } from '@/contexts/ChatInputContext';
import { Urls } from '@/lib/navigation';
import { trackNewChatStarted } from '@/analytics';
import type { SessionEvent } from '@/api/types';
import type { AgentCanvasChatContext } from '@/artifact/types';

interface SessionLifecycleInput {
  currentSessionId: string | null;
  onError: (msg: string) => void;
  collectAgentCanvasContext?: () => Promise<AgentCanvasChatContext | null>;
  clearAgentCanvasContext?: () => void;
}

export const useSessionLifecycle = (input: SessionLifecycleInput) => {
  const {
    currentSessionId,
    onError,
    collectAgentCanvasContext,
    clearAgentCanvasContext,
  } = input;
  const navigate = useNavigate();
  const { setMessage } = useChatInputContext();

  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;

  const chat = useChat({
    currentSessionId,
    onError: useCallback((msg: string) => {
      onError(msg);
    }, [onError]),
    onSessionGone: useCallback((_sessionId: string) => {
      clearLastChat();
      navigate(Urls.home());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    onSessionCreated: useCallback((sessionId: string) => {
      trackNewChatStarted();
      setMessage('');
      navigate(Urls.agent(sessionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setMessage]),
  });

  const client = useOptionalVmClient();
  const chatRef = useRef(chat);
  chatRef.current = chat;

  const reloadTimelineRef = useRef<(sessionId: string) => void>(() => {});

  const chatHistory = useChatHistory({
    onSessionEvent: useCallback((event: SessionEvent) => {
      const c = chatRef.current;
      const activeId = currentSessionIdRef.current;

      if (event.type === 'session_deleted') {
        const wasActive = activeId === event.session_id;
        c.teardownSession(event.session_id);
        if (wasActive) {
          clearLastChat();
          navigate(Urls.home());
        }
        return;
      }

      if (event.type !== 'session_updated') return;

      const isActive = activeId === event.session.id;

      // Clear isRunning for any session whose run completed, even if
      // it's not the active chat.  Without this, switching away from a
      // session while its run is in-flight leaves isRunning=true forever
      // because the per-session SSE is disconnected on chat switch and
      // the done event is never delivered.
      if (!event.session.active_run_id) {
        const chatState = c.getChat(event.session.id);
        if (chatState?.isRunning) {
          c.syncRunStopped(event.session.id);
          if (isActive) {
            reloadTimelineRef.current(event.session.id);
          }
        }
      }

      if (!isActive) return;

      c.ensureSessionStream(event.session.id, event.session.active_run_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  });

  reloadTimelineRef.current = (sessionId: string) => {
    void chatHistory.loadSessionTimeline(sessionId).then((timeline) => {
      if (timeline.length > 0 && currentSessionIdRef.current === sessionId) {
        chatRef.current.hydrateChat(sessionId, { timeline });
      }
    }).catch(() => {});
  };

  // ---- Stale-run detection ----
  useEffect(() => {
    const c = chatRef.current;
    if (!currentSessionId) return;
    const chatState = c.getChat(currentSessionId);
    if (!chatState?.isRunning) return;

    const session = chatHistory.sessions.find((s) => s.id === currentSessionId);
    if (!session || session.active_run_id) return;

    c.syncRunStopped(currentSessionId);
    reloadTimelineRef.current(currentSessionId);
  }, [chatHistory.sessions, currentSessionId]);

  // ---- Visibility-based timeline reload ----
  const chatHistorySessionsRef = useRef(chatHistory.sessions);
  chatHistorySessionsRef.current = chatHistory.sessions;

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const sessionId = currentSessionIdRef.current;
      if (!sessionId) return;

      setTimeout(() => {
        if (currentSessionIdRef.current !== sessionId) return;
        const cs = chatRef.current.getChat(sessionId);
        if (!cs) return;

        const session = chatHistorySessionsRef.current.find(
          (s) => s.id === sessionId,
        );
        if (session?.active_run_id) return;

        if (cs.isRunning) {
          chatRef.current.syncRunStopped(sessionId);
        }

        reloadTimelineRef.current(sessionId);
      }, 3000);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // ---- Power-on detection ----
  const [isPoweringOn, setIsPoweringOn] = useState(false);

  const handleSend = useCallback(
    (message: string, filePaths?: string[]) => {
      void (async () => {
        let agentCanvas: AgentCanvasChatContext | null = null;
        try {
          agentCanvas = message.trim().startsWith('$')
            ? null
            : await collectAgentCanvasContext?.() ?? null;
        } catch (err) {
          onError(err instanceof Error ? err.message : String(err));
          return;
        }
        if (currentSessionId) {
          const session = chatHistory.sessions.find((s) => s.id === currentSessionId);
          if (session && session.runtime_status === 'off' && session.interaction_count > 0) {
            setIsPoweringOn(true);
          }
        }
        chat.sendMessage(message, filePaths, {
          agentCanvas,
          onStarted: agentCanvas ? clearAgentCanvasContext : undefined,
        });
      })();
    },
    [
      chat,
      chatHistory.sessions,
      clearAgentCanvasContext,
      collectAgentCanvasContext,
      currentSessionId,
      onError,
    ],
  );

  useEffect(() => {
    if (!isPoweringOn) return;
    if (chat.activeChat?.error) {
      setIsPoweringOn(false);
      return;
    }
    const session = chatHistory.sessions.find((s) => s.id === currentSessionId);
    if (session && session.runtime_status !== 'off') {
      setIsPoweringOn(false);
    }
  }, [isPoweringOn, currentSessionId, chat.activeChat?.error, chatHistory.sessions]);

  useEffect(() => {
    setIsPoweringOn(false);
  }, [currentSessionId]);

  useEffect(() => {
    if (!isPoweringOn) return;
    const timer = setTimeout(() => setIsPoweringOn(false), 30_000);
    return () => clearTimeout(timer);
  }, [isPoweringOn]);

  return { chat, chatHistory, client, handleSend, isPoweringOn };
};
